/**
 * Bridge listener for the native UploadWorker (BGProcessingTask).
 *
 * Swift posts a `RoavaDrainUploadQueue` CustomEvent on `window` via the Capacitor
 * bridge whenever iOS wakes the app in the background. This module installs a
 * single listener that invokes whatever drain handler the upload subsystem has
 * registered. If nothing is registered yet (e.g. drain logic not wired, or web
 * preview), the event is a no-op — defensive by design so a stray firing can
 * never crash the webview.
 *
 * Usage:
 *   import { installUploadDrainListener, registerUploadDrainHandler } from "@/lib/native/upload-drain";
 *   installUploadDrainListener();                  // once, at app boot
 *   registerUploadDrainHandler(async () => { ... }); // when upload subsystem is ready
 */

export const DRAIN_EVENT = "RoavaDrainUploadQueue";

type DrainHandler = () => void | Promise<void>;

let handler: DrainHandler | null = null;
let installed = false;
let draining = false;

/** Register the function that actually drains the upload queue. Replaces any prior handler. */
export function registerUploadDrainHandler(fn: DrainHandler | null): void {
  handler = fn;
}

/** Install the window-level listener exactly once. Safe to call from SSR/web. */
export function installUploadDrainListener(): void {
  if (installed) return;
  if (typeof window === "undefined") return;
  installed = true;

  window.addEventListener(DRAIN_EVENT, () => {
    void runDrain("native-event");
  });
}

async function runDrain(source: string): Promise<void> {
  if (draining) return; // re-entrancy guard — Swift may fire repeatedly
  if (!handler) {
    // Expected on web and before the upload subsystem registers itself.
    if (typeof console !== "undefined") {
      console.info(`[upload-drain] ${source}: no handler registered, skipping`);
    }
    return;
  }
  draining = true;
  try {
    await handler();
  } catch (err) {
    if (typeof console !== "undefined") {
      console.error("[upload-drain] handler threw", err);
    }
  } finally {
    draining = false;
  }
}

/** Test/manual trigger — useful from a debug button or to validate wiring. */
export function triggerUploadDrain(): void {
  void runDrain("manual");
}
